/**
 * Utility for managing pending HR test state in localStorage.
 * Used when a job seeker comes from a shared test link and needs to complete
 * signup/login before taking the HR test.
 *
 * HR-shared tests bypass skill verification requirement - jobseekers can take
 * these tests directly without being verified.
 */

const PENDING_TEST_KEY = 'vetriaai_pending_test';

export interface PendingTestData {
  testId: string;
  testTitle?: string;
  hrEmail?: string;
  savedAt: number; // timestamp
  bypassVerification: boolean; // HR-shared tests bypass skill verification
}

/**
 * Save pending test ID when user comes from a shared test link
 * bypassVerification is always true for HR-shared links
 */
export const savePendingTest = (testId: string, testTitle?: string, hrEmail?: string): void => {
  const data: PendingTestData = {
    testId,
    testTitle,
    hrEmail,
    savedAt: Date.now(),
    bypassVerification: true, // HR-shared tests always bypass verification
  };
  localStorage.setItem(PENDING_TEST_KEY, JSON.stringify(data));
};

/**
 * Get the pending test data if it exists
 */
export const getPendingTest = (): PendingTestData | null => {
  try {
    const data = localStorage.getItem(PENDING_TEST_KEY);

    if (!data) {
      return null;
    }

    const parsed: PendingTestData = JSON.parse(data);

    // Validate that testId exists and is a non-empty string
    if (!parsed.testId || typeof parsed.testId !== 'string' || parsed.testId.trim() === '') {
      clearPendingTest();
      return null;
    }

    // Check if the pending test is still valid (within 24 hours)
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > ONE_DAY_MS) {
      clearPendingTest();
      return null;
    }

    return parsed;
  } catch (error) {
    clearPendingTest(); // Clear corrupted data
    return null;
  }
};

/**
 * Clear the pending test after it's been taken or no longer needed
 */
export const clearPendingTest = (): void => {
  localStorage.removeItem(PENDING_TEST_KEY);
};

/**
 * Check if there's a pending test
 */
export const hasPendingTest = (): boolean => {
  return getPendingTest() !== null;
};
